import * as React from "react";
import "../App.css";

import "./UserListPage.css";

import moment from "moment-timezone";

import "bootstrap/dist/css/bootstrap.min.css";

import { connect } from "react-redux";
import { IProfile, ILineUpdate, UserID } from "../api/APIClient";
import { fetchProfiles, listTopics, updateLine } from "../thunks";
import { RootState } from "../types";

import { NewCallContainer } from "./NewCallModal";
import { NewPushContainer } from "./NewPushModal";

import {
  toggleCallModalOpen,
  togglePushModalOpen,
  toggleUserModalOpen,
  showUserModal as showUserModalAction,
} from "../actions";
import TimeZoneCell from "src/components/TimeZoneCell";
import VersionCell from "src/components/VersionCell";
import { Checkbox } from "src/components/Checkbox";
import { FilterInput, IFilter } from "src/components/FilterInput";
import ShowUserContainer from "./ShowUserModal";
import ProfileLinePickerContainer from "src/components/ProfileLinePicker";

interface IProps {
  profiles: IProfile[];
  callModalOpen: boolean;
  pushModalOpen: boolean;
  userModalOpen: boolean;

  fetchProfiles: () => void;
  listTopics: () => void;
  updateLine: (update: ILineUpdate) => any;
  onToggleCallModal: () => void;
  onTogglePushModal: () => void;
  onToggleUserModal: () => void;
  showUserModal: (userID: UserID) => any;
}

interface IState {
  selected: Set<number>;
  order: number[];
  sortOpts: ISortOptions;
  lastSelection: number;

  linePickerOpen: boolean;
}

interface IHeader {
  key: keyof IProfile;
  label: string;
}

interface ISortOptions {
  key: keyof IProfile;
  descending: boolean;
  filters: IFilter[];
}

export class UserListPage extends React.Component<IProps, IState> {
  public state: IState = {
    selected: new Set(),
    order: [],
    sortOpts: {
      key: "createdAt",
      descending: true,
      filters: [],
    },
    lastSelection: 0,
    linePickerOpen: false,
  };

  private filterInput: FilterInput | null = null;

  public componentWillMount() {
    this.props.fetchProfiles();
    this.props.listTopics();
  }

  public componentDidUpdate(oldProps: IProps) {
    if (oldProps.profiles !== this.props.profiles) {
      this.setSort(this.state.sortOpts);
    }
  }

  public render() {
    const {
      callModalOpen,
      userModalOpen,
      onToggleCallModal,
      onTogglePushModal,
      onToggleUserModal,
      pushModalOpen,
    } = this.props;
    const { order, linePickerOpen } = this.state;

    const selectedProfiles = this.selectedProfiles();
    const selectedActiveProfiles = selectedProfiles.filter(p => !p.disabled);
    const selectedActiveProfileIDs = selectedActiveProfiles.map(p => p.id);

    return (
      <div className="App">
        <ShowUserContainer
          toggleOpen={onToggleUserModal}
          isOpen={userModalOpen}
        />
        <NewCallContainer
          initialQuery={{
            users: selectedActiveProfileIDs,
          }}
          toggleOpen={onToggleCallModal}
          isOpen={callModalOpen}
        />
        <NewPushContainer
          users={selectedActiveProfiles}
          toggleOpen={onTogglePushModal}
          isOpen={pushModalOpen}
        />

        <div className="header sticky-top">
          <div className="sidebar-left">
            <FilterInput
              ref={this.onFilterInputRef}
              onChange={this.onFilterChange}
            />
          </div>
          <div className="sidebar-right">
            <button
              disabled={selectedProfiles.length === 0}
              onClick={this.onClickLines}
              id="line-button"
              className="btn btn-outline-primary"
              color="primary"
            >
              Lines
            </button>
            <ProfileLinePickerContainer
              target="line-button"
              profiles={selectedProfiles}
              isOpen={linePickerOpen}
            />
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={selectedProfiles.length === 0}
              onClick={onToggleCallModal}
            >
              New Call
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="user-list table table-sm">
            <thead>
              <tr>
                <th onClick={this.onSelectAll} className="checkbox-header">
                  <Checkbox
                    indeterminate={
                      selectedProfiles.length > 0 &&
                      selectedProfiles.length < order.length
                    }
                    checked={
                      selectedProfiles.length === order.length &&
                      order.length > 0
                    }
                  />
                </th>
                {[
                  { key: "username", label: "Username" },
                  { key: "timeZone", label: "Time" },
                  { key: "version", label: "Version" },
                  { key: "createdAt", label: "Joined" },
                  { key: "lines", label: "Lines" },
                ].map(this.renderHeader)}
              </tr>
            </thead>
            <tbody>{order.map(this.renderRow)}</tbody>
          </table>
        </div>
      </div>
    );
  }

  private onFilterInputRef = (ref: FilterInput | null) => {
    this.filterInput = ref;
  };

  private onFilterChange = (filters: IFilter[]) => {
    this.setSort({
      ...this.state.sortOpts,
      filters,
    });
  };

  private renderHeader = (header: IHeader) => {
    const { sortOpts } = this.state;
    return (
      <th key={header.key} data-key={header.key} onClick={this.onClickHeader}>
        {header.label}{" "}
        {sortOpts.key === header.key ? (
          <i
            className={
              "fa fa-fw" +
              (sortOpts.descending ? " fa-sort-down" : " fa-sort-up")
            }
          />
        ) : null}
      </th>
    );
  };

  private renderRow = (idx: number) => {
    const { profiles } = this.props;
    const { selected } = this.state;
    const profile = profiles[idx];

    const {
      createdAt,
      disabled,
      id,
      lines,
      name,
      pushToken,
      timeZone,
      username,
      version,
    } = profile;

    let os = null;
    if (pushToken && pushToken.includes("fcm:")) {
      os = "android";
    } else if (pushToken) {
      os = "ios";
    }

    return (
      <tr
        key={id}
        data-idx={idx}
        className={
          "" +
          (disabled ? " disabled" : "") +
          (selected.has(idx) ? " selected-row" : "")
        }
      >
        <td className="checkbox-cell" data-idx={idx} onClick={this.onSelectRow}>
          {<input checked={selected.has(idx)} type="checkbox" />}
        </td>
        <td className="username-cell">
          <span className="username">{username || name}&nbsp;</span>
        </td>
        <td className="time-zone-cell">
          {timeZone ? <TimeZoneCell timeZone={timeZone} /> : null}
        </td>
        <td className="version-cell">
          {version ? <VersionCell version={version} os={os} /> : null}
        </td>
        <td className="join-cell">
          {moment(createdAt).format("MM/DD/YY hh:mm")}
        </td>
        <td className="lines-cell">
          <div className="lines-cell-inner">{lines.map(this.renderLine)}</div>
        </td>
      </tr>
    );
  };

  private renderLine = (name: string) => {
    return (
      <span
        key={name}
        data-name={name}
        onClick={this.onClickLineTag}
        className="line-tag badge badge-light"
      >
        {name}
      </span>
    );
  };

  private onClickLines = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    this.setState({ linePickerOpen: !this.state.linePickerOpen });
  };

  private onClickLineTag = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.preventDefault();

    const span = e.currentTarget;
    const name = span.dataset.name as string;

    // HACK: don't want to think about design, so just set this
    // imperatively
    if (this.filterInput) {
      this.filterInput.setState({ value: `line:${name}` });
      this.setSort({
        ...this.state.sortOpts,
        filters: [
          {
            key: "line",
            value: name,
          },
        ],
      });
    }
  };

  private onSelectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const { selected, order } = this.state;

    const empty = selected.size === 0;

    selected.clear();

    if (empty) {
      order.forEach(i => {
        selected.add(i);
      });
    }

    this.setState({ selected });
  };

  private onSelectRow = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const { selected, lastSelection, order } = this.state;

    const tr = e.currentTarget;
    const rawIdx = tr.dataset.idx;
    if (!rawIdx) {
      throw new Error("missing data-idx on selection target");
    }
    const idx = parseInt(rawIdx, 10);

    if (e.shiftKey) {
      let lo = order.indexOf(lastSelection);
      // clear if last selection doesn't exist
      if (lo < 0 || lo > order.length) {
        lo = 0;
      }
      let hi = order.indexOf(idx);
      if (lo > hi) {
        [lo, hi] = [hi, lo];
      }

      for (let i = lo; i <= hi; i++) {
        selected.add(order[i]);
      }
    } else {
      if (selected.has(idx)) {
        selected.delete(idx);
      } else {
        selected.add(idx);
      }

      this.setState({
        lastSelection: idx,
      });
    }

    this.setState({
      selected,
    });
  };

  private selectedProfiles = (): IProfile[] => {
    const { selected, sortOpts } = this.state;
    const { profiles } = this.props;
    const { filters } = sortOpts;

    const out: IProfile[] = [];
    selected.forEach(i => {
      const profile = profiles[i];

      if (!matchesFilters(profile, filters)) {
        return;
      }

      out.push(profile);
    });

    return out;
  };

  private onClickHeader = (e: React.MouseEvent<HTMLTableHeaderCellElement>) => {
    const th = e.currentTarget;
    const key = th.dataset.key as keyof IProfile;
    const descending = !this.state.sortOpts.descending;
    this.setSort({
      ...this.state.sortOpts,
      key,
      descending,
    });
  };

  private setSort(sortOpts: ISortOptions) {
    const { profiles } = this.props;
    const { filters } = sortOpts;

    this.setState({ sortOpts });

    let order = [];
    for (let i = 0; i < profiles.length; i++) {
      const profile = profiles[i];

      if (!matchesFilters(profile, filters)) {
        continue;
      }

      order.push(i);
    }

    order = order.sort(this.sortFunc(sortOpts));

    this.setState({ order });
  }

  private sortFunc = (opts: ISortOptions) => (aIdx: number, bIdx: number) => {
    const { profiles } = this.props;

    const a = profiles[aIdx];
    const b = profiles[bIdx];

    const { key, descending } = opts;

    let diff = 0;

    // HACK: use name if there isn't a username yet
    const nameA = a.username || a.name;
    const nameB = b.username || b.name;

    if (key === "username") {
      diff = nameA.localeCompare(nameB);
    } else if (key === "id") {
      diff = a.id.localeCompare(b.id);
    } else if (key === "createdAt") {
      diff = Date.parse(a.createdAt) - Date.parse(b.createdAt);
    } else if (key === "version") {
      if (!a.version && !b.version) {
        diff = 0;
      } else if (!a.version) {
        diff = 1;
      } else if (!b.version) {
        diff = -1;
      } else {
        diff = parseVersion(a.version) - parseVersion(b.version);
      }
    } else if (key === "timeZone") {
      // blank lines always go at the end
      if (!a.timeZone && !b.timeZone) {
        diff = 0;
      } else if (!a.timeZone) {
        diff = 1;
      } else if (!b.timeZone) {
        diff = -1;
        // if not blank, sort by tz
      } else {
        const offsetA = moment.tz(a.timeZone).utcOffset();
        const offsetB = moment.tz(b.timeZone).utcOffset();

        // First sort by TZ offset
        diff = offsetA - offsetB;

        // Then sort by TZ name
        if (diff === 0) {
          diff = a.timeZone.localeCompare(b.timeZone);
        }
        // Then sort by name
        if (diff === 0) {
          diff = nameA.localeCompare(nameB);
        }
      }
    }

    // // Disabled lines always go at the end.
    // if (a.disabled && b.disabled) {
    //   // ok
    // } else if (a.disabled) {
    //   diff = 1;
    // } else if (b.disabled) {
    //   diff = -1;
    // }

    if (descending) {
      diff *= -1;
    }

    return diff;
  };
}

const hasLines = (profile: IProfile, lines: string[]): boolean => {
  for (const a of lines) {
    for (const b of profile.lines) {
      if (a === b) {
        return true;
      }
    }
  }
  return false;
};

const parseVersion = (version: string) => {
  let num = 0; // a single integer containing all the version parts
  const parts = version.split(".").reverse(); // work minor to major
  for (let i = 0; i < parts.length; i++) {
    let int = parseInt(parts[i], 10); // get the integer version number
    int *= Math.pow(10, i * 3 + 2); // give each part 3 digits
    num += int;
  }
  return num;
};

const matchesFilters = (profile: IProfile, filters: IFilter[]): boolean => {
  if (filters.length === 0) {
    return true;
  }

  for (const filter of filters) {
    switch (filter.key) {
      case "id":
        if (profile.id === filter.value) {
          return true;
        }
        break;

      case "line":
        if (hasLines(profile, [filter.value])) {
          return true;
        }
        break;
    }
  }

  return false;
};

export const UserListContainer = connect(
  ({ profiles, calls, push }: RootState) => ({
    callModalOpen: calls.modalOpen,
    profiles: [...profiles.data],
    pushModalOpen: push.modalOpen,
    userModalOpen: profiles.modalOpen,
  }),
  dispatch => ({
    // HACK: <any>
    fetchProfiles: () => dispatch<any>(fetchProfiles()),
    listTopics: () => dispatch<any>(listTopics()),
    updateLine: (update: ILineUpdate) => dispatch<any>(updateLine(update)),
    onToggleCallModal: () => dispatch<any>(toggleCallModalOpen()),
    onTogglePushModal: () => dispatch<any>(togglePushModalOpen()),
    onToggleUserModal: () => dispatch<any>(toggleUserModalOpen()),
    showUserModal: (userID: UserID) =>
      dispatch<any>(showUserModalAction(userID)),
  })
)(UserListPage);

export default UserListContainer;



// WEBPACK FOOTER //
// ./src/pages/UserListPage.tsx